import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Mock user data for development
  const mockUser = {
    uid: 'mock-user-123',
    email: 'demo@endgame.com',
    displayName: 'Chess Demo User',
    rating: 1847,
    avatar: '♟️',
    createdAt: new Date('2022-01-01')
  };

  useEffect(() => {
    // Simulate Firebase auth state change
    const timer = setTimeout(() => {
      // For development, auto-login with mock user
      setUser(mockUser);
      setIsAuthenticated(true);
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true);
      // Simulate login delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In real app, would use Firebase Auth
      setUser(mockUser);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (email, password, displayName) => {
    try {
      setLoading(true);
      // Simulate signup delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In real app, would use Firebase Auth
      const newUser = {
        ...mockUser,
        email,
        displayName,
        uid: `user-${Date.now()}`
      };
      
      setUser(newUser);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      // In real app, would use Firebase Auth signOut
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setUser(null);
      setIsAuthenticated(false);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email) => {
    try {
      // In real app, would use Firebase Auth sendPasswordResetEmail
      await new Promise(resolve => setTimeout(resolve, 1000));
      return { success: true };
    } catch (error) {
      console.error('Password reset error:', error);
      return { success: false, error: error.message };
    }
  };

  const updateProfile = async (updates) => {
    try {
      setLoading(true);
      // In real app, would update Firebase user profile and Firestore
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setUser(prevUser => ({
        ...prevUser,
        ...updates
      }));
      
      return { success: true };
    } catch (error) {
      console.error('Profile update error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    signup,
    logout,
    resetPassword,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};